import React, { useState } from 'react';
import { View, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from '@/components/ui/text';
import { useTranslation } from '../../hooks/useTranslation';
import EditableTagList from './EditableTagList';
import { SchoolFrameworkStep, ICF_COMPETENCY_KEYS } from '../../types/schoolFramework';
import { brand } from '../../design-system/theme/tokens';

interface FrameworkStepEditorProps {
  step: SchoolFrameworkStep;
  frameworkType: string;
  index: number;
  totalSteps: number;
  onChange: (step: SchoolFrameworkStep) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}

export default function FrameworkStepEditor({
  step,
  frameworkType,
  index,
  totalSteps,
  onChange,
  onMoveUp,
  onMoveDown,
  onDelete,
}: FrameworkStepEditorProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const subSteps = step.sub_steps || [];
  const concepts = step.concepts || [];
  const positiveMarkers = step.positive_markers || [];
  const negativeMarkers = step.negative_markers || [];
  const sampleQuestions = step.sample_questions || [];
  const detailSummary = [
    `${subSteps.length} ${frameworkType === 'step_model' ? t('schoolManagement.frameworksSection.subStepsShort') : t('schoolManagement.frameworksSection.behaviorDimensionsShort')}`,
    `${positiveMarkers.length} ${t('schoolManagement.frameworksSection.positiveMarkersShort')}`,
    `${negativeMarkers.length} ${t('schoolManagement.frameworksSection.negativeMarkersShort')}`,
    `${sampleQuestions.length} ${t('schoolManagement.frameworksSection.sampleQuestionsShort')}`,
  ].join(' • ');

  const update = (partial: Partial<SchoolFrameworkStep>) => {
    onChange({ ...step, ...partial });
  };

  const updateName = (lang: string, value: string) => {
    update({ name: { ...step.name, [lang]: value } });
  };

  return (
    <View style={styles.container}>
      {/* Header — always visible */}
      <TouchableOpacity style={styles.header} onPress={() => setExpanded(!expanded)} activeOpacity={0.7}>
        <View style={styles.headerLeft}>
          <View style={styles.orderBadge}>
            <Text style={styles.orderText}>{index + 1}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.stepTitle} numberOfLines={1}>
              {step.name?.tr || step.name?.en || step.step_key}
            </Text>
            <Text style={styles.detailSummary} numberOfLines={1}>
              {detailSummary}
            </Text>
            {subSteps.length > 0 && (
              <Text style={styles.previewSubStep} numberOfLines={1}>
                ↳ {subSteps[0]}
              </Text>
            )}
            {step.icf_competency_mapping && (
              <Text style={styles.icfBadge}>{step.icf_competency_mapping}</Text>
            )}
          </View>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity onPress={onMoveUp} disabled={index === 0} hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}>
            <MaterialIcons name="keyboard-arrow-up" size={22} color={index === 0 ? '#D1D5DB' : '#6B7280'} />
          </TouchableOpacity>
          <TouchableOpacity onPress={onMoveDown} disabled={index === totalSteps - 1} hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}>
            <MaterialIcons name="keyboard-arrow-down" size={22} color={index === totalSteps - 1 ? '#D1D5DB' : '#6B7280'} />
          </TouchableOpacity>
          <MaterialIcons name={expanded ? 'expand-less' : 'expand-more'} size={22} color="#6B7280" />
        </View>
      </TouchableOpacity>

      {/* Expanded body */}
      {expanded && (
        <View style={styles.body}>
          {/* Step key */}
          <View style={styles.field}>
            <Text style={styles.label}>step_key</Text>
            <TextInput
              style={styles.input}
              value={step.step_key}
              onChangeText={(v) => update({ step_key: v })}
              placeholder="e.g. goal_setting"
              placeholderTextColor="#9CA3AF"
            />
          </View>

          {/* Name TR */}
          <View style={styles.field}>
            <Text style={styles.label}>{t('schoolManagement.frameworksSection.stepNameTr')}</Text>
            <TextInput
              style={styles.input}
              value={step.name?.tr || ''}
              onChangeText={(v) => updateName('tr', v)}
              placeholder="Türkçe ad"
              placeholderTextColor="#9CA3AF"
            />
          </View>

          {/* Name EN */}
          <View style={styles.field}>
            <Text style={styles.label}>{t('schoolManagement.frameworksSection.stepNameEn')}</Text>
            <TextInput
              style={styles.input}
              value={step.name?.en || ''}
              onChangeText={(v) => updateName('en', v)}
              placeholder="English name"
              placeholderTextColor="#9CA3AF"
            />
          </View>

          {/* Description */}
          <View style={styles.field}>
            <Text style={styles.label}>{t('schoolManagement.frameworksSection.stepDescription')}</Text>
            <TextInput
              style={[styles.input, styles.multiline]}
              value={step.description || ''}
              onChangeText={(v) => update({ description: v })}
              multiline
              numberOfLines={3}
              placeholder="..."
              placeholderTextColor="#9CA3AF"
            />
          </View>

          {/* Sub-steps / behavior dimensions */}
          <View style={styles.field}>
            <Text style={styles.label}>
              {frameworkType === 'step_model'
                ? t('schoolManagement.frameworksSection.subSteps')
                : t('schoolManagement.frameworksSection.behaviorDimensions')}
            </Text>
            <EditableTagList
              tags={subSteps}
              onChange={(v) => update({ sub_steps: v })}
              color={frameworkType === 'step_model' ? '#2563EB' : '#7C3AED'}
            />
          </View>

          {/* Concepts */}
          <View style={styles.field}>
            <Text style={styles.label}>{t('schoolManagement.frameworksSection.concepts')}</Text>
            <EditableTagList
              tags={concepts}
              onChange={(v) => update({ concepts: v })}
              color="#6366F1"
            />
          </View>

          {/* Positive Markers */}
          <View style={styles.field}>
            <Text style={styles.label}>{t('schoolManagement.frameworksSection.positiveMarkers')}</Text>
            <EditableTagList
              tags={positiveMarkers}
              onChange={(v) => update({ positive_markers: v })}
              color="#10B981"
            />
          </View>

          {/* Negative Markers */}
          <View style={styles.field}>
            <Text style={styles.label}>{t('schoolManagement.frameworksSection.negativeMarkers')}</Text>
            <EditableTagList
              tags={negativeMarkers}
              onChange={(v) => update({ negative_markers: v })}
              color="#EF4444"
            />
          </View>

          {/* Sample Questions */}
          <View style={styles.field}>
            <Text style={styles.label}>{t('schoolManagement.frameworksSection.sampleQuestions')}</Text>
            <EditableTagList
              tags={sampleQuestions}
              onChange={(v) => update({ sample_questions: v })}
              color="#F59E0B"
            />
          </View>

          {/* ICF Mapping */}
          <View style={styles.field}>
            <Text style={styles.label}>{t('schoolManagement.frameworksSection.icfMapping')}</Text>
            <View style={styles.icfOptions}>
              <TouchableOpacity
                style={[styles.icfChip, !step.icf_competency_mapping && styles.icfChipActive]}
                onPress={() => update({ icf_competency_mapping: null })}
              >
                <Text style={[styles.icfChipText, !step.icf_competency_mapping && styles.icfChipTextActive]}>
                  {t('schoolManagement.frameworksSection.icfMappingNone')}
                </Text>
              </TouchableOpacity>
              {ICF_COMPETENCY_KEYS.map((key) => (
                <TouchableOpacity
                  key={key}
                  style={[styles.icfChip, step.icf_competency_mapping === key && styles.icfChipActive]}
                  onPress={() => update({ icf_competency_mapping: key })}
                >
                  <Text style={[styles.icfChipText, step.icf_competency_mapping === key && styles.icfChipTextActive]}>
                    {key.replace(/_/g, ' ')}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Delete */}
          <TouchableOpacity style={styles.deleteButton} onPress={onDelete}>
            <MaterialIcons name="delete-outline" size={18} color="#EF4444" />
            <Text style={styles.deleteText}>{t('schoolManagement.frameworksSection.deleteStep')}</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    marginBottom: 10,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 14,
    backgroundColor: '#F9FAFB',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 10,
  },
  orderBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: brand.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  orderText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700',
  },
  stepTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  detailSummary: {
    fontSize: 11,
    color: '#4B5563',
    marginTop: 2,
  },
  previewSubStep: {
    fontSize: 12,
    color: '#1F2937',
    marginTop: 2,
  },
  icfBadge: {
    fontSize: 11,
    color: '#6366F1',
    marginTop: 2,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  body: {
    padding: 16,
    gap: 14,
  },
  field: {
    gap: 4,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 2,
  },
  input: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16, // >=16 stops iOS focus-zoom (FE-17)
    color: '#111827',
    backgroundColor: '#FFFFFF',
  },
  multiline: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  icfOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  icfChip: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    backgroundColor: '#F9FAFB',
  },
  icfChipActive: {
    backgroundColor: brand.primary,
    borderColor: brand.primary,
  },
  icfChipText: {
    fontSize: 12,
    color: '#4B5563',
  },
  icfChipTextActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: '#FEF2F2',
    marginTop: 4,
  },
  deleteText: {
    fontSize: 13,
    color: '#EF4444',
    fontWeight: '500',
  },
});
